import React, { useEffect, useState } from "react";
import apiClient from "src/utils/apiClient";

interface HelpImageProps {
  src?: string;
  alt?: string;
}

/**
 * Renders an image referenced by the help markdown. Markdown references images
 * by relative path under `images/`, e.g. `images/cage-label.png`. The backend
 * endpoint is GET /help/assets/{path} where {path} is relative to the images/ 
 * prefix, so we strip a leading `images/` before requesting.
 */
const HelpImage: React.FC<HelpImageProps> = ({ src, alt }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    setObjectUrl(null);
    if (!src) return;
    let cancelled = false;
    let createdUrl: string | null = null;

    const assetPath = src.replace(/^\/+/, "").replace(/^images\//, "");

    const load = async () => {
      try {
        const res = await apiClient.get(
          `/help/assets/${assetPath.split("/").map(encodeURIComponent).join("/")}`,
          {
            responseType: "blob",
          },
        );
        if (cancelled) return;
        createdUrl = URL.createObjectURL(res.data as Blob);
        setObjectUrl(createdUrl);
      } catch {
        // Leave unrendered on failure (broken images are not shown).
      }
    };

    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [src]);

  if (!objectUrl) return null;
  return <img src={objectUrl} alt={alt || ""} />;
};

export default HelpImage;
